import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { agentProjects, agentResearch, openSourceProjects, productCases, type ProductCase } from '@/content/portfolio'
import { ProjectDiagram, type ProjectVisual } from '@/components/sections/cv/ProjectDiagram'
import { SpotlightCard } from '@/components/ui/SpotlightCard'
import { useReducedMotion } from '@/lib/useReducedMotion'
import { Projects } from '@/components/sections/cv/Projects'

const projectVisuals: Record<string, ProjectVisual> = {
  'creative-ai-workspace': 'creative',
  'multi-agent-evaluation': 'playstat',
  'Meeting transcript agent': 'ontology',
  ScrumClaw: 'scrum',
  MarketingClaw: 'marketing',
  'Content engine claw': 'content',
}

function CaseCard({ item, featured = false }: { item: ProductCase; featured?: boolean }) {
  const { hash } = useLocation()
  const id = featured ? `featured-${item.id}` : item.id
  const [expanded, setExpanded] = useState(hash === `#${id}`)
  const reducedMotion = useReducedMotion()
  const screenshots = item.evidence?.sources.filter((source) => /\.(webp|png|jpe?g)$/i.test(source.href)) ?? []
  const [selectedImage, setSelectedImage] = useState(0)
  const links = item.links ?? (item.href ? [{ label: 'Visit product', href: item.href }] : [])

  return (
    <article
      id={id}
      className="scroll-mt-28 overflow-hidden rounded-xl border border-vibe-border bg-card-bg transition-colors focus-within:border-accent-primary/60"
    >
      <SpotlightCard>
        {item.image && (
          <div className="aspect-video border-b border-vibe-border bg-vibe-elevated">
            <img
              src={item.image}
              alt={item.imageAlt}
              width={1280}
              height={720}
              loading={featured ? 'eager' : 'lazy'}
              className="h-full w-full object-contain"
            />
          </div>
        )}
        {!item.image && projectVisuals[item.id] && (
          <div className="px-5 pt-1">
            <ProjectDiagram visual={projectVisuals[item.id]} animate />
          </div>
        )}
        <div className="p-5 lg:p-7">
          <div className="section-label text-accent-primary">
            {item.category} · {item.maturity}
          </div>
          <h3 className="mt-3 text-2xl font-medium tracking-tight text-text-heading">{item.title}</h3>
          {(item.context || item.period) && (
            <p className="mt-2 font-mono text-xs leading-relaxed text-text-secondary">
              {item.context}
              {item.context && item.period && ' · '}
              {item.period}
            </p>
          )}
          <p className="mt-4 text-[15px] leading-relaxed text-text-secondary">{item.overview}</p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={`${id}-story`}
              onClick={() => setExpanded((value) => !value)}
              className="rounded border border-accent-primary/50 px-4 py-2.5 text-sm text-accent-primary transition-colors hover:bg-accent-primary/10"
            >
              {expanded ? 'Close project −' : 'View project +'}
              <span className="sr-only">: {item.title}</span>
            </button>
            {links[0] && (
              <a
                href={links[0].href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 py-2 text-sm text-accent-primary underline underline-offset-4 hover:text-text-primary"
              >
                {links[0].label}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only"> (opens in new tab)</span>
              </a>
            )}
          </div>
          <div id={`${id}-story`}>
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  key="story"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: reducedMotion ? 0 : 0.22 }}
                  className="overflow-hidden"
                >
                  <dl className="mt-6 space-y-5 border-t border-vibe-border pt-5 text-sm leading-relaxed text-text-primary/80">
                    {item.context && (
                      <div>
                        <dt className="section-label mb-1">My role</dt>
                        <dd>{item.context}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="section-label mb-1">{item.id === 'sme-advisory' ? 'Scope' : 'What I shipped'}</dt>
                      <dd>{item.shipped}</dd>
                    </div>
                    {item.decision && (
                      <div>
                        <dt className="section-label mb-1">Key decision</dt>
                        <dd>{item.decision}</dd>
                      </div>
                    )}
                    {item.outcome && (
                      <div>
                        <dt className="section-label mb-1">Outcome</dt>
                        <dd>{item.outcome}</dd>
                      </div>
                    )}
                  </dl>
                  {links.length > 1 && (
                    <div className="mt-6 flex flex-wrap gap-x-5 gap-y-3">
                      {links.slice(1).map((link) => (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-accent-primary underline underline-offset-4 hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-primary"
                        >
                          {link.label}
                          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only"> (opens in new tab)</span>
                        </a>
                      ))}
                    </div>
                  )}
                  {item.evidence && (
                    <div className="mt-6 border-t border-vibe-border pt-5">
                      <p className="text-sm leading-relaxed text-text-primary">{item.evidence.metrics}</p>
                      <p className="mt-2 text-xs leading-relaxed text-text-secondary">{item.evidence.note}</p>
                      {screenshots.length > 1 && (
                        <figure className="mt-5">
                          <div className="aspect-video overflow-hidden rounded border border-vibe-border bg-vibe-elevated">
                            <motion.img
                              key={screenshots[selectedImage].href}
                              initial={{ opacity: reducedMotion ? 1 : 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.18 }}
                              src={screenshots[selectedImage].href}
                              alt={`${item.title}: ${screenshots[selectedImage].label}`}
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <figcaption className="mt-3 flex flex-wrap gap-2">
                            {screenshots.map((source, index) => (
                              <button
                                key={source.href}
                                type="button"
                                aria-pressed={selectedImage === index}
                                onClick={() => setSelectedImage(index)}
                                className={`rounded border px-3 py-2 text-xs ${selectedImage === index ? 'border-accent-primary text-accent-primary' : 'border-vibe-border text-text-secondary hover:text-text-primary'}`}
                              >
                                {source.label}
                              </button>
                            ))}
                          </figcaption>
                        </figure>
                      )}
                      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3">
                        {item.evidence.sources.map((source) => (
                          <a
                            key={source.label}
                            href={source.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-accent-primary underline underline-offset-4 hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-primary"
                          >
                            {source.label}
                            <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                            <span className="sr-only"> (opens in new tab)</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </SpotlightCard>
    </article>
  )
}

const groups = [
  { id: 'product-work', category: 'Product software', label: 'Employer', title: 'Employer products' },
  { id: 'ai-products', category: 'AI product', label: 'AI', title: 'AI products' },
  { id: 'side-projects', category: 'Side project', label: 'Independent', title: 'Side projects & advisory' },
] as const

export function SelectedWork() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { hash } = useLocation()
  const reducedMotion = useReducedMotion()
  const filters = [
    { id: 'all', label: 'All', count: productCases.length + agentProjects.length + openSourceProjects.length },
    ...groups.map((group) => ({
      ...group,
      count: productCases.filter((item) => item.category === group.category).length,
    })),
    { id: 'agents', label: 'Experiments', count: agentProjects.length },
    { id: 'open-source', label: 'Open source', count: openSourceProjects.length },
  ]
  const requested = params.get('category') ?? hash.slice(1)
  const active = filters.find((filter) => filter.id === requested)?.id ?? 'all'
  const count = filters.find((filter) => filter.id === active)!.count
  const featured = ['vietcetera-platform', '96booth'].map((id) => productCases.find((item) => item.id === id)!)

  return (
    <>
      <section aria-label="Featured projects" className="mx-auto max-w-6xl px-4 pb-12 lg:px-8 lg:pb-16">
        <div className="grid items-start gap-6 md:grid-cols-[1.2fr_1fr]">
          {featured.map((item) => (
            <CaseCard key={item.id} item={item} featured />
          ))}
        </div>
      </section>

      <section
        id="project-library"
        aria-labelledby="library-heading"
        className="scroll-mt-24 border-t border-vibe-border pb-16"
      >
        <div className="mx-auto max-w-6xl px-4 pt-10 lg:px-8">
          <div className="flex items-baseline justify-between gap-4">
            <h2 id="library-heading" className="text-3xl text-text-heading">
              Project library
            </h2>
            <span aria-live="polite" role="status" className="font-mono text-xs text-text-secondary">
              {count} projects
            </span>
          </div>
        </div>
        <div className="sticky top-20 z-20 border-b border-vibe-border bg-vibe-black/95 backdrop-blur-sm">
          <div
            role="group"
            aria-label="Filter projects"
            className="mx-auto flex max-w-6xl gap-2 overflow-x-auto px-4 py-4 lg:px-8"
          >
            {filters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                aria-pressed={active === filter.id}
                aria-controls="project-results"
                onClick={() => {
                  const next = new URLSearchParams(params)
                  next.set('category', filter.id)
                  navigate({ search: next.toString(), hash: '#project-library' }, { preventScrollReset: true })
                }}
                className={`shrink-0 rounded-full border px-4 py-2 font-mono text-xs transition-colors ${active === filter.id ? 'border-accent-primary bg-accent-primary/10 text-accent-primary' : 'border-vibe-border text-text-secondary hover:border-text-secondary hover:text-text-primary'}`}
              >
                {filter.label} <span className="ml-1 opacity-70">{filter.count}</span>
              </button>
            ))}
          </div>
        </div>
        <motion.div
          id="project-results"
          key={active}
          initial={reducedMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {groups
            .filter((group) => active === 'all' || active === group.id)
            .map((group) => (
              <section key={group.id} id={group.id} className="scroll-mt-40 pt-8">
                <div className="mx-auto max-w-6xl px-4 lg:px-8">
                  <h2 className="mb-5 font-mono text-sm text-text-secondary">{group.title}</h2>
                  <div className="grid items-start gap-6 md:grid-cols-2">
                    {productCases
                      .filter((item) => item.category === group.category)
                      .map((item) => (
                        <CaseCard key={`${item.id}-${hash}`} item={item} />
                      ))}
                  </div>
                </div>
              </section>
            ))}

          {(active === 'all' || active === 'agents') && (
            <section id="agents" className="scroll-mt-40 pt-10">
              <div className="mx-auto max-w-6xl px-4 lg:px-8">
                <h2 className="text-3xl font-medium tracking-tight text-text-heading">Personal agents & experiments</h2>
                <p className="mt-4 text-base text-text-secondary">
                  Tools I use, prototypes I test, and questions I’m still researching.
                </p>
                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  {agentProjects.map((project) => (
                    <article key={project.name} className="rounded-xl border border-vibe-border bg-card-bg p-5 lg:p-6">
                      <div className="section-label text-accent-primary">{project.label}</div>
                      <h3 className="mt-3 text-xl font-medium text-text-heading">{project.name}</h3>
                      <ProjectDiagram visual={projectVisuals[project.name]} animate />
                      <p className="mt-4 text-sm leading-relaxed text-text-secondary">{project.description}</p>
                    </article>
                  ))}
                </div>
                <aside
                  className="mt-8 rounded-xl border border-vibe-border p-5 lg:p-6"
                  aria-labelledby="research-heading"
                >
                  <h3 id="research-heading" className="text-xl font-medium text-text-heading">
                    Research & learning
                  </h3>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {agentResearch.map((topic) => (
                      <div key={topic.name}>
                        <h4 className="font-medium text-text-heading">{topic.name}</h4>
                        <p className="mt-2 text-sm leading-relaxed text-text-secondary">{topic.description}</p>
                      </div>
                    ))}
                  </div>
                </aside>
              </div>
            </section>
          )}
          {(active === 'all' || active === 'open-source') && <Projects />}
        </motion.div>
      </section>
    </>
  )
}
